/**
 * The edit surface for one listing setting (AC-7): the existing ui/sheet, on the right at `md` and
 * above (~480px) and as a bottom sheet below. Radix traps focus and closes on Escape; on close,
 * focus goes back to the control that opened it (`returnFocusRef`, the MoreMenu pattern). The
 * hosted editor supplies its own Save / Cancel and calls its `onDone` to close the sheet.
 */

import type { ReactNode, RefObject } from 'react';
import { X } from 'lucide-react';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { cn } from '../../../lib/utils';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';

interface SettingEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  returnFocusRef: RefObject<HTMLElement>;
  testId: string;
  /** 'wide' for an editor that needs room at md+ (the availability grid editor); default ~480px. */
  size?: 'default' | 'wide';
}

export function SettingEditSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  returnFocusRef,
  testId,
  size = 'default',
}: SettingEditSheetProps) {
  const isMdUp = useMediaQuery('(min-width: 768px)');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMdUp ? 'right' : 'bottom'}
        data-testid={testId}
        // The primitive's own 16px close icon is hidden (below 44px); the header renders a 44px one.
        className={cn(
          'flex flex-col gap-0 p-0 bg-card [&>button:first-child]:hidden',
          isMdUp
            ? cn('w-full', size === 'wide' ? 'sm:max-w-3xl' : 'sm:max-w-[480px]')
            : 'max-h-[90dvh] rounded-t-xl'
        )}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <SheetHeader className="shrink-0 flex-row items-start justify-between gap-3 space-y-0 border-b border-border px-6 py-4 text-left">
          <div className="min-w-0">
            <SheetTitle className="text-lg font-semibold">{title}</SheetTitle>
            {description ? (
              <SheetDescription className="mt-1">{description}</SheetDescription>
            ) : (
              <SheetDescription className="sr-only">Edit the {title} setting for this listing</SheetDescription>
            )}
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid={`${testId}-close`}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
